/**
 * Por que a fonte está onde está (`TP-067`, refino `D11`/`D12`): a frase da coluna "Situação" da aba Fontes.
 *
 * A API calcula o código (`SourceSituation` em `GET /fontes/{id}`); aqui ele vira uma frase que diz o que
 * aconteceu e o que fazer. A pergunta que essa frase responde: "a fonte está em Recebida há dois dias — é a
 * Function desligada, um erro da IA ou fila?".
 */

import { formatarDataHora, formatarRelativo, parseInstanteOuNulo } from '../../../shared/dominio/datas';
import type { EstadoDaFonte } from './tipos';

// ------------------------------------------------------------- forma da API

type CodigoDaSituacaoApi =
  | 'Active'
  | 'Rejected'
  | 'AwaitingReview'
  | 'ReaderFailed'
  | 'ReaderNotPolling'
  | 'Queued'
  | 'StalledWithoutError';

type MotivoDaFalhaApi = 'AiUnavailable' | 'Timeout' | 'InvalidResponse' | 'ApiRejected' | 'Other';

/** Schema `SourceSituation`. */
export interface SituacaoDaFonteApi {
  readonly code: CodigoDaSituacaoApi;
  readonly since: string | null;
  readonly stage: string | null;
  readonly reason: MotivoDaFalhaApi | null;
  readonly detail: string | null;
  readonly pendingRules: number;
  readonly pendingItems: number;
}

// -------------------------------------------------------- domínio do front

export type CodigoDaSituacao =
  | 'ativa'
  | 'rejeitada'
  | 'aguardando_revisao'
  | 'leitor_falhou'
  | 'leitor_sem_consultar'
  | 'na_fila'
  | 'parada_sem_erro';

export interface SituacaoDaFonte {
  readonly codigo: CodigoDaSituacao;
  /** Desde quando vale; `null` em `leitor_sem_consultar` = o leitor nunca consultou. */
  readonly desde: string | null;
  readonly etapa: string | null;
  readonly motivo: string | null;
  readonly detalhe: string | null;
  readonly regrasPendentes: number;
  readonly itensPendentes: number;
}

const CODIGO: Record<CodigoDaSituacaoApi, CodigoDaSituacao> = {
  Active: 'ativa',
  Rejected: 'rejeitada',
  AwaitingReview: 'aguardando_revisao',
  ReaderFailed: 'leitor_falhou',
  ReaderNotPolling: 'leitor_sem_consultar',
  Queued: 'na_fila',
  StalledWithoutError: 'parada_sem_erro',
};

const MOTIVO: Record<MotivoDaFalhaApi, string> = {
  AiUnavailable: 'a IA não respondeu',
  Timeout: 'o tempo esgotou',
  InvalidResponse: 'a IA respondeu fora do formato',
  ApiRejected: 'a API recusou o resultado',
  Other: 'erro inesperado',
};

const ETAPA: Readonly<Record<string, string>> = {
  texto: 'extração do texto',
  mapa: 'mapa das páginas',
  clausulas: 'montagem das cláusulas',
  regras: 'leitura das regras',
};

export function mapearSituacao(bruto: SituacaoDaFonteApi): SituacaoDaFonte {
  return {
    codigo: CODIGO[bruto.code],
    desde: bruto.since,
    etapa: bruto.stage,
    motivo: bruto.reason === null ? null : MOTIVO[bruto.reason],
    detalhe: bruto.detail,
    regrasPendentes: bruto.pendingRules,
    itensPendentes: bruto.pendingItems,
  };
}

function quando(instante: string | null): string {
  const data = parseInstanteOuNulo(instante);
  return data === null ? '' : `${formatarRelativo(data)} (${formatarDataHora(data)})`;
}

/** "22/10/2026 13:56 (há 55 minutos)": depois de "desde", a data vem primeiro. */
function desde(instante: string | null): string {
  const data = parseInstanteOuNulo(instante);
  return data === null ? '' : `${formatarDataHora(data)} (${formatarRelativo(data)})`;
}

function etapa(token: string | null): string {
  return token === null ? 'leitura' : (ETAPA[token] ?? token);
}

/**
 * A frase da situação. `null` para a fonte ativa ou recusada: nas duas o estado e o motivo da recusa (já em
 * outra coluna) bastam.
 */
export function fraseDaSituacao(situacao: SituacaoDaFonte, estado: EstadoDaFonte): string | null {
  switch (situacao.codigo) {
    case 'ativa':
    case 'rejeitada':
      return null;
    case 'aguardando_revisao': {
      const partes = [
        situacao.regrasPendentes > 0 ? `${String(situacao.regrasPendentes)} regras` : null,
        situacao.itensPendentes > 0 ? `${String(situacao.itensPendentes)} itens` : null,
      ].filter((parte): parte is string => parte !== null);
      return partes.length > 0
        ? `Lida. Esperando revisão: ${partes.join(' e ')}.`
        : 'Lida e revisada. Falta ativar.';
    }
    case 'leitor_falhou':
      return `A leitura parou na etapa "${etapa(situacao.etapa)}" ${quando(situacao.desde)}: ${situacao.motivo ?? 'erro inesperado'}.${
        situacao.detalhe !== null ? ` ${situacao.detalhe}` : ''
      }`;
    case 'leitor_sem_consultar':
      return situacao.desde === null
        ? 'O leitor de contratos nunca consultou esta API: a Function do compilador não está ligada a este ambiente ou não alcança a API.'
        : `O leitor de contratos não consulta a fila desde ${desde(situacao.desde)}: a Function do compilador pode estar desligada, sem acesso à API ou com configuração faltando (URL ou token do gateway de IA).`;
    case 'na_fila':
      // Recebida só anda quando o leitor consulta a fila; nas outras, a fonte acabou de passar de um passo.
      return estado === 'recebida'
        ? `Na fila do leitor, enviada ${quando(situacao.desde)}.`
        : `Em leitura agora: o último passo terminou ${quando(situacao.desde)}.`;
    case 'parada_sem_erro':
      return estado === 'mapeada'
        ? `Sem avançar ${quando(situacao.desde)}, sem erro registrado. O ciclo automático não retoma uma fonte em "Mapeada": reprocesse a partir das cláusulas.`
        : `Sem avançar ${quando(situacao.desde)}, sem erro registrado, embora o leitor esteja consultando a fila.`;
  }
}

/** A situação pede atenção (tom de alerta na coluna)? */
export function situacaoPedeAtencao(situacao: SituacaoDaFonte): boolean {
  return situacao.codigo === 'leitor_falhou' || situacao.codigo === 'leitor_sem_consultar' || situacao.codigo === 'parada_sem_erro';
}
